import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Image as ImageIcon, Pencil, Film } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getApiUrl } from "@/utils/api";
import { resolveImageUrl } from "@/utils/imageUrl";
import ImageEditorModal from "@/components/admin/ImageEditorModal";
import RendyImageOptionsDialog from "@/components/admin/RendyImageOptionsDialog";
import RendyVideoPanel from "@/components/admin/RendyVideoPanel";

const API = getApiUrl();

/** Rightmove media_type integer codes — mirrors the backend GalleryItem shape. */
export const MEDIA_TYPE_OPTIONS = [
  { value: 1, label: "Photograph" },
  { value: 2, label: "Floorplan" },
  { value: 3, label: "EPC" },
  { value: 4, label: "Brochure" },
  { value: 5, label: "Video" },
  { value: 6, label: "Virtual Tour" },
];

/** Normalise a mixed gallery (string URLs OR objects) into GalleryItem shape. */
export function normaliseGallery(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, idx) => {
    if (item && typeof item === "object") {
      return {
        media_url: item.media_url || "",
        media_type: Number(item.media_type) || 1,
        caption: item.caption || "",
        sort_order: Number.isFinite(item.sort_order) ? item.sort_order : idx,
        use_for_rendy: !!item.use_for_rendy,
        poster: item.poster || null,
        duration: Number.isFinite(item.duration) ? item.duration : null,
        rendy_preset_key: item.rendy_preset_key || null,
        rendy_staging_input_state: item.rendy_staging_input_state || null,
        rendy_staging_style_id: item.rendy_staging_style_id || null,
        rendy_staging_animation_id: item.rendy_staging_animation_id || null,
        rendy_camera_action_key: item.rendy_camera_action_key || null,
      };
    }
    const url = typeof item === "string" ? item : "";
    const isVideo = /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);
    return {
      media_url: url,
      media_type: isVideo ? 5 : 1,
      caption: "",
      sort_order: idx,
      use_for_rendy: false,
      rendy_preset_key: null,
      rendy_staging_input_state: null,
      rendy_staging_style_id: null,
      rendy_staging_animation_id: null,
      rendy_camera_action_key: null,
    };
  });
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "--:--";
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h ? String(m).padStart(2, "0") : String(m);
  return `${h ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}

function SortableRow({ item, index, onChange, onRemove, onEdit, onOpenRendyOptions, selected, onToggleSelect, showRendyToggle }) {
  const [loadedDuration, setLoadedDuration] = useState(null);
  const duration = Number.isFinite(item.duration) ? item.duration : loadedDuration;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.__key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isVideo = item.media_type === 5 || /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(item.media_url || "");
  const rendyEligible = item.media_type === 1 && !!item.media_url;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`grid ${showRendyToggle ? "grid-cols-[auto_96px_1fr_130px_2fr_80px_auto_auto_auto]" : "grid-cols-[auto_96px_1fr_130px_2fr_80px_auto_auto]"} gap-2 items-center border p-2 bg-background ${
        item.use_for_rendy ? "border-blue-400 bg-blue-50/40" : "border-border"
      }`}
      data-testid={`gallery-row-${index}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        title="Drag to reorder"
        data-testid={`gallery-drag-${index}`}
      >
        <GripVertical size={18} />
      </button>

      <div
        className={`w-24 h-16 bg-black/5 flex items-center justify-center overflow-hidden relative ${
          !isVideo && item.media_url ? "cursor-pointer group" : ""
        }`}
        onClick={() => !isVideo && item.media_url && onEdit(index)}
        title={!isVideo && item.media_url ? "Click to rotate, crop, or flip this image" : undefined}
        data-testid={`gallery-preview-${index}`}
      >
        {item.media_url ? (
          isVideo ? (
            <>
              <video
                src={resolveImageUrl(item.media_url)}
                poster={item.poster ? resolveImageUrl(item.poster) : undefined}
                className="w-full h-full object-cover"
                muted
                playsInline
                preload="metadata"
                onLoadedMetadata={(e) => setLoadedDuration(e.currentTarget.duration)}
              />
              <span
                className="absolute bottom-0.5 right-0.5 bg-black/75 text-white text-[10px] leading-none px-1 py-0.5 rounded-sm font-mono"
                title="Video duration"
                data-testid={`gallery-video-duration-${index}`}
              >
                {formatDuration(duration)}
              </span>
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onToggleSelect(item.__key)}
                onPointerDown={(e) => e.stopPropagation()}
                className="absolute top-0.5 left-0.5 h-4 w-4 accent-destructive cursor-pointer"
                title="Select this video for bulk deletion"
                aria-label="Select video"
                data-testid={`gallery-video-select-${index}`}
              />
            </>
          ) : (
            <>
              <img src={resolveImageUrl(item.media_url)} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <Pencil size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </>
          )
        ) : (
          <ImageIcon size={20} className="text-muted-foreground" />
        )}
      </div>

      <Input
        value={item.media_url}
        onChange={(e) => onChange(index, { media_url: e.target.value })}
        placeholder="https://…"
        className="rounded-none h-9 text-xs"
        data-testid={`gallery-url-${index}`}
      />

      <select
        value={item.media_type}
        onChange={(e) => onChange(index, { media_type: Number(e.target.value) })}
        className="rounded-none h-9 border border-input bg-background px-2 text-xs"
        data-testid={`gallery-type-${index}`}
      >
        {MEDIA_TYPE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.value} — {o.label}</option>
        ))}
      </select>

      <Input
        value={item.caption}
        onChange={(e) => onChange(index, { caption: e.target.value })}
        placeholder={isVideo ? "Describe the video, e.g. Drone flythrough of the garden" : "Describe the image, e.g. Spacious open-plan kitchen"}
        className="rounded-none h-9 text-xs"
        data-testid={`gallery-caption-${index}`}
      />

      <Input
        type="number"
        value={item.sort_order}
        onChange={(e) => onChange(index, { sort_order: Number(e.target.value) })}
        className="rounded-none h-9 text-xs"
        data-testid={`gallery-order-${index}`}
      />

      <button
        type="button"
        onClick={() => onEdit(index)}
        disabled={isVideo || !item.media_url}
        className="text-primary hover:text-primary/80 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
        title={isVideo ? "Editing videos isn't supported" : "Rotate, crop, or flip this image"}
        data-testid={`gallery-edit-${index}`}
      >
        <Pencil size={16} />
      </button>

      {showRendyToggle && (
        <button
          type="button"
          onClick={() => rendyEligible && onOpenRendyOptions(index)}
          disabled={!rendyEligible}
          className={`p-1 rounded-sm border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
            item.use_for_rendy
              ? "bg-blue-600 text-white border-blue-600"
              : "text-muted-foreground border-transparent hover:border-blue-400 hover:text-blue-600"
          }`}
          title={
            !rendyEligible
              ? "Only photographs can be used in Rendy videos"
              : item.use_for_rendy
              ? "Marked for Rendy — click to edit or remove"
              : "Include this photo in a Rendy video"
          }
          data-testid={`gallery-rendy-toggle-${index}`}
        >
          <Film size={16} />
        </button>
      )}

      <button
        type="button"
        onClick={() => onRemove(index)}
        className="text-destructive hover:text-destructive/80 p-1"
        title="Remove"
        data-testid={`gallery-remove-${index}`}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

/**
 * `value` is the current gallery (array of GalleryItem-like objects or legacy strings).
 * `onChange` is called with the new normalised gallery whenever the user edits/reorders/removes.
 */
export function GalleryEditor({ value, onChange, isMasterAdmin = false, propertyId, showRendyPanel = true }) {
  const buildRows = (v) =>
    normaliseGallery(v).map((it, i) => ({ ...it, __key: `${it.media_url || "row"}-${i}-${Math.random().toString(36).slice(2, 8)}` }));

  const [rows, setRows] = useState(() => buildRows(value));
  const [editIndex, setEditIndex] = useState(null);
  const [rendyOptionsIndex, setRendyOptionsIndex] = useState(null);
  // Tracks the last gallery we emitted so the sync effect below can tell an
  // "echo" of our own change (parent handing the same value straight back)
  // apart from a genuinely new gallery (e.g. switching property). Without
  // this, every click/edit regenerated all row keys and force-remounted the
  // whole list, which reset the modal's scroll position on every click.
  const lastEmitted = useRef(JSON.stringify(normaliseGallery(value)));

  // Sync when parent hands us a genuinely new gallery (e.g. a different property).
  useEffect(() => {
    const incoming = JSON.stringify(normaliseGallery(value));
    if (incoming === lastEmitted.current) return;
    lastEmitted.current = incoming;
    setRows(buildRows(value));
  }, [JSON.stringify(value)]);

  const emit = (next) => {
    setRows(next);
    const cleaned = next.map(({ __key, ...rest }) => rest);
    lastEmitted.current = JSON.stringify(cleaned);
    onChange(cleaned);
  };

  const handleFieldChange = (index, patch) => {
    const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    emit(next);
  };

  const handleRemove = (index) => {
    const item = rows[index];
    const next = rows.filter((_, i) => i !== index).map((r, i) => ({ ...r, sort_order: i }));
    emit(next);
    setSelectedKeys((prev) => {
      if (!item || !prev.has(item.__key)) return prev;
      const n = new Set(prev);
      n.delete(item.__key);
      return n;
    });
    // Rendy videos are also written to the gallery from outside this form
    // (the video dashboard, the auto-add poller) — persist the removal
    // immediately instead of waiting on the property form's debounced
    // autosave, which could otherwise resurrect it with a stale array.
    if (item?.media_type === 5 && item.media_url && propertyId) {
      axios
        .post(`${API}/rendy/properties/${propertyId}/gallery/remove-urls`, { urls: [item.media_url] }, { withCredentials: true })
        .catch(() => toast.error("Removed here, but failed to save — it may reappear. Please retry."));
    }
  };

  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const isVideoRow = (r) => r.media_type === 5 || /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(r.media_url || "");
  const videoRows = rows.filter(isVideoRow);
  const selectedVideoRows = videoRows.filter((r) => selectedKeys.has(r.__key));

  const toggleSelect = (key) =>
    setSelectedKeys((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const toggleSelectAllVideos = () =>
    setSelectedKeys(
      selectedVideoRows.length === videoRows.length ? new Set() : new Set(videoRows.map((r) => r.__key))
    );

  const handleBulkDeleteVideos = () => {
    if (!selectedVideoRows.length) return;
    if (!window.confirm(`Delete ${selectedVideoRows.length} selected video${selectedVideoRows.length === 1 ? "" : "s"} from the gallery?`)) return;
    const keys = new Set(selectedVideoRows.map((r) => r.__key));
    const urls = selectedVideoRows.map((r) => r.media_url).filter(Boolean);
    const next = rows.filter((r) => !keys.has(r.__key)).map((r, i) => ({ ...r, sort_order: i }));
    emit(next);
    setSelectedKeys(new Set());
    if (urls.length && propertyId) {
      axios
        .post(`${API}/rendy/properties/${propertyId}/gallery/remove-urls`, { urls }, { withCredentials: true })
        .then(() => toast.success(`${urls.length} video${urls.length === 1 ? "" : "s"} removed from the gallery`))
        .catch(() => toast.error("Removed here, but failed to save — they may reappear. Please retry."));
    } else {
      toast.success(`${selectedVideoRows.length} video${selectedVideoRows.length === 1 ? "" : "s"} removed`);
    }
  };

  const handleAdd = () => {
    const next = [
      ...rows,
      {
        media_url: "",
        media_type: 1,
        caption: "",
        sort_order: rows.length,
        use_for_rendy: false,
        __key: `new-${Date.now()}`,
      },
    ];
    emit(next);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.__key === active.id);
    const newIndex = rows.findIndex((r) => r.__key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(rows, oldIndex, newIndex).map((r, i) => ({ ...r, sort_order: i }));
    emit(reordered);
  };

  const rendyCount = rows.filter((r) => r.use_for_rendy).length;

  return (
    <div className="space-y-2" data-testid="gallery-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {showRendyPanel && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Film size={13} className="text-blue-600" />
            {rendyCount > 0
              ? `${rendyCount} photo${rendyCount === 1 ? "" : "s"} marked for Rendy video generation.`
              : "No photos marked for Rendy yet — click the film icon on a photo to include it."}
          </p>
        )}
        {propertyId && showRendyPanel && (
          <RendyVideoPanel
            propertyId={propertyId}
            isMasterAdmin={isMasterAdmin}
            onAdded={(clips) => {
              const existing = new Set(rows.map((r) => r.media_url));
              const additions = clips
                .filter((c) => c.url && !existing.has(c.url))
                .map((c, i) => ({
                  media_url: c.url,
                  media_type: 5,
                  caption: "Rendy AI Video",
                  sort_order: rows.length + i,
                  use_for_rendy: false,
                  poster: c.poster || null,
                  duration: Number.isFinite(c.duration) ? c.duration : null,
                  __key: `rendy-add-${c.url}-${i}`,
                }));
              if (additions.length) emit([...rows, ...additions]);
            }}
            onRemoved={(urls) => {
              const urlSet = new Set(urls);
              const next = rows.filter((r) => !urlSet.has(r.media_url)).map((r, i) => ({ ...r, sort_order: i }));
              if (next.length !== rows.length) emit(next);
            }}
          />
        )}
      </div>
      {videoRows.length > 0 && (
        <div
          className="flex items-center justify-between gap-3 flex-wrap border border-dashed border-border bg-muted/30 px-3 py-2"
          data-testid="gallery-video-bulk-bar"
        >
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 accent-destructive"
              checked={videoRows.length > 0 && selectedVideoRows.length === videoRows.length}
              onChange={toggleSelectAllVideos}
              data-testid="gallery-video-select-all"
            />
            <span>
              Select all videos
              <span className="text-muted-foreground"> · {selectedVideoRows.length} of {videoRows.length} selected</span>
            </span>
          </label>
          <button
            type="button"
            onClick={handleBulkDeleteVideos}
            disabled={!selectedVideoRows.length}
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-destructive text-destructive hover:bg-destructive/5 rounded-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="gallery-video-bulk-delete-btn"
          >
            <Trash2 size={13} /> Delete selected video{selectedVideoRows.length === 1 ? "" : "s"}
          </button>
        </div>
      )}
      <div className={`grid ${showRendyPanel ? "grid-cols-[auto_96px_1fr_130px_2fr_80px_auto_auto_auto]" : "grid-cols-[auto_96px_1fr_130px_2fr_80px_auto_auto]"} gap-2 items-center text-xs font-medium text-muted-foreground px-2`}>
        <span />
        <Label className="text-xs">Preview</Label>
        <Label className="text-xs">media_url</Label>
        <Label className="text-xs">media_type</Label>
        <Label className="text-xs">caption</Label>
        <Label className="text-xs">sort_order</Label>
        <span />
        {showRendyPanel && <Label className="text-xs">Rendy</Label>}
        <span />
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rows.map((r) => r.__key)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {rows.length === 0 ? (
              <div className="text-sm text-muted-foreground italic p-4 text-center border border-dashed border-border">
                No gallery items. Add one below.
              </div>
            ) : (
              rows.map((r, i) => (
                <SortableRow
                  key={r.__key}
                  item={r}
                  index={i}
                  onChange={handleFieldChange}
                  onRemove={handleRemove}
                  onEdit={setEditIndex}
                  onOpenRendyOptions={setRendyOptionsIndex}
                  selected={selectedKeys.has(r.__key)}
                  onToggleSelect={toggleSelect}
                  showRendyToggle={showRendyPanel}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        onClick={handleAdd}
        className="text-xs px-3 py-1.5 border border-primary text-primary hover:bg-primary/5 rounded-none transition-colors"
        data-testid="gallery-add-btn"
      >
        + Add media
      </button>

      <ImageEditorModal
        open={editIndex != null}
        onClose={() => setEditIndex(null)}
        sourceUrl={editIndex != null ? resolveImageUrl(rows[editIndex]?.media_url || "") : ""}
        onSaved={(newUrl) => {
          if (editIndex == null) return;
          const next = rows.map((r, i) => (i === editIndex ? { ...r, media_url: newUrl } : r));
          emit(next);
          setEditIndex(null);
        }}
      />

      <RendyImageOptionsDialog
        open={rendyOptionsIndex != null}
        onClose={() => setRendyOptionsIndex(null)}
        initial={rendyOptionsIndex != null ? rows[rendyOptionsIndex] : null}
        propertyId={propertyId}
        followingCount={
          rendyOptionsIndex != null
            ? rows.filter((r, i) => i > rendyOptionsIndex && r.media_type !== 5).length
            : 0
        }
        onSave={(patch, { applyToFollowing = false } = {}) => {
          if (rendyOptionsIndex == null) return;
          const item = rows[rendyOptionsIndex];
          // Photos only — never mark video entries for Rendy.
          const targetIdx = new Set(
            rows
              .map((r, i) => i)
              .filter((i) => i === rendyOptionsIndex || (applyToFollowing && i > rendyOptionsIndex && rows[i].media_type !== 5))
          );
          const next = rows.map((r, i) => (targetIdx.has(i) ? { ...r, ...patch } : r));
          emit(next);
          // Persist right away — the property form's autosave excludes
          // `gallery`, so without this a Rendy generation started before an
          // explicit "Save" click would run against stale, unmarked data.
          if (item?.media_url && propertyId) {
            const mediaUrls = [...targetIdx].map((i) => rows[i].media_url).filter(Boolean);
            axios
              .post(
                `${API}/rendy/properties/${propertyId}/gallery/set-rendy-options`,
                { media_url: item.media_url, media_urls: mediaUrls, ...patch },
                { withCredentials: true }
              )
              .then(() => {
                if (applyToFollowing && mediaUrls.length > 1) {
                  toast.success(`Rendy settings applied to ${mediaUrls.length} photos`);
                }
              })
              .catch(() => toast.error("Marked here, but failed to save — please click Save to be sure."));
          }
        }}
        onRemove={() => {
          if (rendyOptionsIndex == null) return;
          const item = rows[rendyOptionsIndex];
          const clearPatch = {
            use_for_rendy: false,
            rendy_preset_key: null,
            rendy_staging_input_state: null,
            rendy_staging_style_id: null,
            rendy_staging_animation_id: null,
            rendy_camera_action_key: null,
          };
          const next = rows.map((r, i) => (i === rendyOptionsIndex ? { ...r, ...clearPatch } : r));
          emit(next);
          if (item?.media_url && propertyId) {
            axios
              .post(
                `${API}/rendy/properties/${propertyId}/gallery/set-rendy-options`,
                { media_url: item.media_url, ...clearPatch },
                { withCredentials: true }
              )
              .catch(() => toast.error("Removed here, but failed to save — please click Save to be sure."));
          }
        }}
      />
    </div>
  );
}

export default GalleryEditor;
